import { useEffect, useState } from "react";
import { useAuth } from "../../../../context/contextAuth";
import { useDesign } from "../../../../context/contextDesign";
import { useAwsUrl } from "../../../../context/Dashboard/aws/contextUrl";
import { useChat } from "../../../../context/contextChat";
import { useThemeMode } from "../../../../context/contextTheme";
import CustomModal from "../../../../components/modal";
import hexToRGBA from "../../../../utils/hexToRGBA";
import { downloadAwsS3CSV } from "../../download";

const AwsUrlTable = () => {
  const { bHeightVal, zoom } = useDesign();
  const { theme } = useThemeMode();
  const { user } = useAuth();
  const { webResults, currentIndex, setCurrentIndex } = useAwsUrl();
  const { setPrompt, setShowChat } = useChat();

  const [vulTable, setVulTable] = useState([]);
  const [modalContent, setModalContent] = useState("");
  const [modalType, setModalType] = useState("");
  const [showModal, setShowModal] = useState(false);

  // --- Risk Level Stylings ---
  const getRiskStyle = (risk) => {
    const r = risk?.toLowerCase() || "";
    if (r.includes("high")) return { bg: hexToRGBA("#dc3545", 0.1), color: "#dc3545" };
    if (r.includes("medium")) return { bg: hexToRGBA("#ffc107", 0.1), color: "#856404" };
    if (r.includes("low")) return { bg: hexToRGBA("#198754", 0.1), color: "#198754" };
    return { bg: hexToRGBA(theme.textMuted, 0.1), color: theme.textMuted };
  };

  const truncateText = (text, wordLimit = 12) => {
    if (!text) return "";
    const words = text.split(" ");
    return words.length > wordLimit ? words.slice(0, wordLimit).join(" ") + "..." : text;
  };

  const handleReadMore = (content, type) => {
    setModalContent(content);
    setModalType(type);
    setShowModal(true);
  };

  const handleAIAssistance = () => {
    if (!modalContent) return;
    const formattedData = `Provide a technical breakdown for a ${user?.designation}. Focus on AWS S3 remediation. \nDescription: "${modalContent}"`.trim();
    setPrompt(formattedData);
    setShowChat(true);
    setShowModal(false);
  };

  useEffect(() => {
    if (Array.isArray(webResults) && webResults.length > 0 && currentIndex !== null) {
      const scan = webResults[currentIndex];
      setVulTable(scan?.vuln_data || []);
    }
  }, [currentIndex, webResults]);

  return (
    <div className="d-flex flex-column h-100 overflow-hidden" style={{ zoom }}>
      {(!webResults || webResults.length === 0) ? (
        <div className="flex-grow-1 d-flex flex-column justify-content-center align-items-center opacity-50">
          <i className="bi bi-bucket-fill display-1 mb-3"></i>
          <h4 className="fw-bold">AWS S3 Scan not yet started</h4>
        </div>
      ) : (
        <div className="d-flex flex-column h-100">

          {/* HEADER & HISTORY SELECTOR */}
          <div className="px-4 py-3 flex-shrink-0 border-bottom d-flex align-items-center justify-content-between" style={{ backgroundColor: theme.background }}>
            <div className="d-flex align-items-center gap-3">
              <span className="small fw-bold text-muted text-uppercase">Bucket History:</span>
              <div className="d-flex bg-light p-1 rounded-3 overflow-auto no-scrollbar">
                {webResults.map((scan, index) => (
                  <button
                    key={scan.scan_uuid || index}
                    className="border-0 px-4 py-1 rounded-2 fw-bold transition-all"
                    style={{
                      fontSize: "0.75rem",
                      whiteSpace: "nowrap",
                      backgroundColor: currentIndex === index ? "#344266" : "transparent",
                      color: currentIndex === index ? "white" : theme.textMuted,
                      boxShadow: currentIndex === index ? "0 2px 6px rgba(0,0,0,0.15)" : "none",
                    }}
                    onClick={() => setCurrentIndex(index)}
                  >
                    Scan {index + 1}
                  </button>
                ))}
              </div>
            </div>
            <div className="d-flex gap-2">
              <button
                className="btn btn-sm btn-outline-primary d-flex align-items-center gap-2 fw-bold"
                onClick={() => downloadAwsS3CSV(webResults, 0)}
              >
                <i className="bi bi-download"></i> Download CSV
              </button>
              {/* <div className="badge bg-primary-subtle text-primary border border-primary-subtle px-3 py-2">
                Total Vulnerabilities: {vulTable.length}
              </div> */}
            </div>
          </div>

          {/* VULNERABILITY DATA GRID */}
          <div className="flex-grow-1 px-4 py-3 overflow-hidden">
            <div className="card border-0 rounded-4 shadow-sm h-100 overflow-hidden" style={{ backgroundColor: theme.background }}>
              <div className="table-responsive h-100 custom-scrollbar">
                <table className="table align-middle mb-0" style={{ color: theme.textColor }}>
                  <thead style={{ position: "sticky", top: 0, zIndex: 10, backgroundColor: "#f8f9fa" }}>
                    <tr className="small text-muted text-uppercase fw-bolder" style={{ fontSize: '0.65rem', letterSpacing: '0.5px' }}>
                      <th className="ps-4 border-0 py-3">Security Alert</th>
                      <th className="text-center border-0 py-3">Risk</th>
                      <th className="text-center border-0 py-3">Confidence</th>
                      <th className="border-0 py-3" style={{ width: "25%" }}>Description</th>
                      <th className="border-0 py-3" style={{ width: "25%" }}>Remediation</th>
                      <th className="pe-4 border-0 py-3 text-end">Ref</th>
                    </tr>
                  </thead>
                  <tbody>
                    {vulTable.map((vuln, index) => {
                      const risk = getRiskStyle(vuln.risk);
                      return (
                        <tr key={index} className="native-row-hover" style={{ borderBottom: `1px solid ${hexToRGBA(theme.textColor, 0.05)}` }}>
                          <td className="ps-4 py-3">
                            <div className="fw-bold" style={{ fontSize: "0.85rem" }}>{vuln.alert}</div>
                          </td>
                          <td className="text-center">
                            <span className="badge rounded-pill px-3 py-1" style={{ backgroundColor: risk.bg, color: risk.color, fontSize: '0.65rem' }}>
                              {vuln.risk}
                            </span>
                          </td>
                          <td className="text-center small opacity-75">{vuln.confidence}</td>
                          <td className="small py-3">
                            <span className="text-muted">{truncateText(vuln.description)}</span>
                            <button onClick={() => handleReadMore(vuln.description, "description")} className="btn btn-link p-0 ms-1 fw-bold text-decoration-none" style={{ fontSize: "0.7rem", color: "#344266" }}>view</button>
                          </td>
                          <td className="small py-3">
                            <span className="text-muted">
                              {Array.isArray(vuln.solution) ? truncateText(vuln.solution.join(" ")) : truncateText(vuln.solution)}
                            </span>
                            <button onClick={() => handleReadMore(Array.isArray(vuln.solution) ? vuln.solution.join(" ") : vuln.solution, "solution")} className="btn btn-link p-0 ms-1 fw-bold text-decoration-none" style={{ fontSize: "0.7rem", color: "#344266" }}>fix</button>
                          </td>
                          <td className="pe-4 py-3 text-end">
                            <a href={vuln.reference} target="_blank" rel="noopener noreferrer" className="btn btn-sm btn-light border-0">
                              <i className="bi bi-box-arrow-up-right text-primary"></i>
                            </a>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* MODAL */}
      <CustomModal
        show={showModal}
        onClose={() => setShowModal(false)}
        title={modalType === "description" ? "Technical Analysis" : "Solution Steps"}
        footer={
          <div className="d-flex gap-2">
            <button className="btn btn-light fw-bold" onClick={() => setShowModal(false)}>Close</button>
            {modalType === "description" && (
              <button className="btn btn-primary px-4 fw-bold shadow-sm" onClick={handleAIAssistance}>
                <i className="bi bi-stars me-2"></i>Generate AI Fix
              </button>
            )}
          </div>
        }
      >
        <div className="p-2 text-light" style={{ whiteSpace: "pre-wrap", fontSize: 13, lineHeight: "1.6", color: "#444" }}>
          {modalContent}
        </div>
      </CustomModal>

      <style>{`
        .native-row-hover:hover {
          background-color: ${hexToRGBA("#344266", 0.02)};
        }
        .no-scrollbar::-webkit-scrollbar { display: none; }
        .custom-scrollbar::-webkit-scrollbar { width: 6px; }
        .custom-scrollbar::-webkit-scrollbar-thumb { 
          background: rgba(52, 66, 102, 0.1); 
          border-radius: 10px; 
        }
      `}</style>
    </div>
  );
};

export default AwsUrlTable;